"use client";

import { useTranslations } from "@/lib/compat/i18n";

/**
 * RealWorldMap — geographically accurate (recognisable) continent outlines
 * for the BR ↔ UAE corridor view, with an equirectangular projection helper.
 *
 * Approach: continents are defined as arrays of [lon, lat] vertex pairs
 * derived from coastal waypoints. Vertex counts are intentionally low (15–
 * 35 per continent) — enough to read as the right continent, small enough
 * to keep the bundle under 10 KB. No npm map deps; no tile server.
 *
 * Projection: equirectangular (linear lon→x, lat→y, no curvature). The
 * corridor span (60°W → 70°E, 50°S → 40°N) sits comfortably without
 * distortion at the latitudes we render.
 */

export type Viewport = {
  /** SVG viewBox width  (px) */ w: number;
  /** SVG viewBox height (px) */ h: number;
  /** west bound (degrees)   */ minLon: number;
  /** east bound (degrees)   */ maxLon: number;
  /** south bound (degrees)  */ minLat: number;
  /** north bound (degrees)  */ maxLat: number;
};

export const CORRIDOR_VIEWPORT: Viewport = {
  w:       640,
  h:       360,
  minLon: -65,
  maxLon:  75,
  minLat: -50,
  maxLat:  42,
};

export function project(lon: number, lat: number, vp: Viewport = CORRIDOR_VIEWPORT): { x: number; y: number } {
  const x = ((lon - vp.minLon) / (vp.maxLon - vp.minLon)) * vp.w;
  const y = ((vp.maxLat - lat) / (vp.maxLat - vp.minLat)) * vp.h; // y inverted (north up)
  return { x, y };
}

// Continent coastline outlines — [lon, lat] vertex arrays, clockwise.
// Vertex count tuned for shape recognition without bloat. Open polygons are
// closed implicitly when stringified into a `<path d="M … Z" />`.

const SOUTH_AMERICA: [number, number][] = [
  // East coast (north → south)
  [-50,   5], [-44,  -2], [-38,  -7], [-35, -13], [-39, -16], [-41, -22], [-43, -23],
  [-46, -24], [-49, -29], [-58, -34], [-62, -39], [-67, -45], [-70, -51], [-68, -54],
  // South + Cape Horn
  [-66, -55],
  // West coast (south → north)
  [-71, -52], [-74, -45], [-73, -36], [-71, -28], [-70, -20], [-72, -13], [-77, -10],
  [-80,  -5], [-81,   0], [-78,   2], [-72,  10], [-67,  10], [-62,   8], [-55,   6],
  [-52,   5],
];

const AFRICA: [number, number][] = [
  // North coast (Tangier → Suez)
  [-9,  35], [-2,  35], [ 3,  36], [ 8,  37], [10,  36], [11,  33], [12,  32], [17,  32],
  [20,  31], [25,  31], [28,  31], [32,  31], [33,  28], [33,  23],
  // Red Sea / Horn (Suez → Mogadishu)
  [37,  22], [39,  15], [42,  13], [44,  12], [49,  11], [51,  10], [50,   3],
  // East coast (Mogadishu → Cape Town)
  [44,  -2], [40,  -3], [40, -15], [35, -20], [32, -26], [28, -30], [25, -34], [20, -35],
  // West coast (Cape Town → Tangier)
  [17, -33], [14, -22], [12, -10], [13,  -5], [ 9,   4], [ 2,   4], [-3,   5], [-6,   5],
  [-9,   8], [-14, 12], [-17, 14], [-16, 21], [-13, 27], [-10, 30], [-6,  33], [-9,  35],
];

const ARABIA: [number, number][] = [
  // Northern edge (Aqaba → Basra) — Sinai/Levant simplified
  [35, 29], [38, 32], [41, 35], [46, 36], [48, 33], [48, 30],
  // Gulf coast (Basra → Strait of Hormuz)
  [49, 28], [50, 26], [51, 25], [53, 25], [55, 25], [56, 24], [57, 22],
  // Indian Ocean / Arabian Sea (Hormuz → Aden)
  [59, 22], [58, 19], [55, 17], [52, 15], [49, 13], [45, 12],
  // Red Sea coast (Aden → Aqaba)
  [43, 13], [42, 15], [40, 18], [39, 21], [37, 22], [37, 25], [35, 28], [35, 29],
];

const INDIA_SLIVER: [number, number][] = [
  // Just the western edge — east bound of our viewport
  [62, 25], [66, 25], [68, 24], [69, 22], [72, 19], [73, 16], [74, 14], [76, 10],
  [78,  8], [78, 12], [80, 13], [82, 17], [83, 21], [84, 25], [82, 25], [78, 28],
  [75, 35], [70, 35], [66, 32], [62, 28], [62, 25],
];

const MADAGASCAR: [number, number][] = [
  [44, -12], [50, -15], [50, -23], [47, -26], [44, -22], [43, -16], [44, -12],
];

const ALL_CONTINENTS: { id: string; verts: [number, number][] }[] = [
  { id: "south-america", verts: SOUTH_AMERICA },
  { id: "africa",        verts: AFRICA },
  { id: "arabia",        verts: ARABIA },
  { id: "india",         verts: INDIA_SLIVER },
  { id: "madagascar",    verts: MADAGASCAR },
];

function toPathString(verts: [number, number][], vp: Viewport): string {
  return (
    verts
      .map(([lon, lat], i) => {
        const { x, y } = project(lon, lat, vp);
        return `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
      })
      .join(" ") + " Z"
  );
}

/**
 * Render the basemap into an existing SVG (intended to live inside the
 * parent's viewBox). The parent should declare `viewBox="0 0 640 360"`
 * (CORRIDOR_VIEWPORT.w × CORRIDOR_VIEWPORT.h) for projection alignment.
 */
export function RealWorldMap({ vp = CORRIDOR_VIEWPORT }: { vp?: Viewport }) {
  const t = useTranslations("realWorldMap");
  return (
    <g aria-label={t("ariaLabel")}>
      {/* Ocean background */}
      <defs>
        <linearGradient id="rwm-ocean" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="var(--orbitra-ink)"       stopOpacity="0.95" />
          <stop offset="100%" stopColor="var(--orbitra-teal-deep)" stopOpacity="0.55" />
        </linearGradient>
      </defs>

      <rect x="0" y="0" width={vp.w} height={vp.h} fill="url(#rwm-ocean)" rx="8" />

      {/* Faint graticule — 15° lat/lon */}
      <g stroke="var(--orbitra-muted)" strokeOpacity="0.10" strokeWidth="0.5">
        {meridians(vp).map((m) => (
          <line key={`m${m}`} x1={projectLonX(m, vp)} y1="0" x2={projectLonX(m, vp)} y2={vp.h} />
        ))}
        {parallels(vp).map((p) => (
          <line key={`p${p}`} x1="0" y1={projectLatY(p, vp)} x2={vp.w} y2={projectLatY(p, vp)} />
        ))}
      </g>

      {/* Continents */}
      <g
        fill="var(--orbitra-teal-deep)"
        fillOpacity="0.88"
        stroke="var(--orbitra-teal-dark)"
        strokeOpacity="0.55"
        strokeWidth="0.6"
        strokeLinejoin="round"
      >
        {ALL_CONTINENTS.map((c) => (
          <path key={c.id} d={toPathString(c.verts, vp)} />
        ))}
      </g>
    </g>
  );
}

function meridians(vp: Viewport): number[] {
  const out: number[] = [];
  for (let lon = Math.ceil(vp.minLon / 15) * 15; lon <= vp.maxLon; lon += 15) out.push(lon);
  return out;
}
function parallels(vp: Viewport): number[] {
  const out: number[] = [];
  for (let lat = Math.ceil(vp.minLat / 15) * 15; lat <= vp.maxLat; lat += 15) out.push(lat);
  return out;
}
function projectLonX(lon: number, vp: Viewport): number {
  return ((lon - vp.minLon) / (vp.maxLon - vp.minLon)) * vp.w;
}
function projectLatY(lat: number, vp: Viewport): number {
  return ((vp.maxLat - lat) / (vp.maxLat - vp.minLat)) * vp.h;
}
